import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Card, Modal, Nav } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { useAuth } from 'react-oidc-context';
import { useSelector, useDispatch } from 'react-redux';
import {
  prevState,
  nextState,
  reset,
  setCenterCoor
} from 'reducers/landDevReducer';
import { ADD_PROJECT, GET_PROJECTS } from 'graphql/project';
import FalconCloseButton from 'components/common/FalconCloseButton';
import NavItem from 'components/common/NavItem';
import SummaryReport from '../summary-report';
import SelectLand from './select-land';
import ProjectDescription from './description';

const CreateProjectModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const auth = useAuth();

  const {
    // control,
    register,
    // watch,
    formState: { errors },
    setError,
    clearErrors,
    handleSubmit,
    reset: resetForm
  } = useForm();

  const { steps, currentState, selectedLandParcels, plantingDataSource } =
    useSelector(state => state.landDev);

  const handleClose = () => {
    onClose();
    dispatch(reset());
    resetForm();
  };

  const [showHelpModal1, setShowHelpModal1] = useState(false);
  const [showHelpModal2, setShowHelpModal2] = useState(false);

  const closeHelpModal1 = () => {
    setShowHelpModal1(false);
  };
  const openHelpModal1 = () => {
    setShowHelpModal1(true);
  };
  const closeHelpModal2 = () => {
    setShowHelpModal2(false);
  };
  const openHelpModal2 = () => {
    setShowHelpModal2(true);
  };

  const handlePrevious = () => {
    if (selectedLandParcels.length[0] !== null) {
      // set location and zoom if selected parcel exist
      const sumLat = selectedLandParcels[0].coordinates.reduce(
        (acc, value) => acc + value[0],
        0
      );
      const sumLng = selectedLandParcels[0].coordinates.reduce(
        (acc, value) => acc + value[1],
        0
      );
      const location = [
        sumLat / selectedLandParcels[0].coordinates.length,
        sumLng / selectedLandParcels[0].coordinates.length
      ];
      dispatch(setCenterCoor(location));
    }
    dispatch(prevState());
  };

  const [mutateFunction] = useMutation(ADD_PROJECT, {
    refetchQueries: () => [
      {
        query: GET_PROJECTS,
        variables: {
          createdBy: auth.user.profile.sub
        }
      }
    ]
  });

  const submit = data => {
    if (currentState < steps.length) {
      dispatch(nextState());
    } else {
      handleClose();

      const parcels = selectedLandParcels.map((p, index) => ({
        parcelNumber: index + 1,
        coordinates: p.coordinates.map(p => ({
          lat: p[0],
          lng: p[1]
        }))
      }));

      mutateFunction({
        variables: {
          input: {
            name: data.name,
            description: data.description,
            parcels: parcels
          }
        },
        onCompleted: () => {
          toast.success('Project created');
        },
        onError: () => {
          toast.error('Error creating project. Please try again.');
        }
      });
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        fullscreen
      >
        <Modal.Header>
          <Modal.Title>Create a project</Modal.Title>
          <FalconCloseButton onClick={handleClose} />
        </Modal.Header>
        <Modal.Body>
          <Card
            className={
              currentState === 3
                ? 'theme-wizard min-h-100'
                : 'theme-wizard h-100'
            }
          >
            <Card.Header className={classNames('bg-body-tertiary', 'pb-2')}>
              <Nav className="justify-content-center">
                {steps.map((item, index) => (
                  <NavItem
                    key={item.label}
                    index={index + 1}
                    step={currentState}
                    stepCount={steps.length}
                    handleNavs={() => {}}
                    icon={item.icon}
                    label={item.label}
                  />
                ))}
              </Nav>
            </Card.Header>
            <Card.Body className="fw-normal px-md-6 py-4">
              {(currentState === 1 || currentState === 2) && (
                <SelectLand
                  currentState={currentState}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                />
              )}
              {currentState === 3 && <SummaryReport />}
              {currentState === 4 && (
                <ProjectDescription register={register} errors={errors} />
              )}
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button
            className="ms-2"
            variant="outline-secondary"
            onClick={() => {
              if (currentState === 1) {
                openHelpModal1();
              } else {
                openHelpModal2();
              }
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              height="20px"
              width="20px"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                {' '}
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12 9C11.7015 9 11.4344 9.12956 11.2497 9.33882C10.8843 9.75289 10.2523 9.79229 9.83827 9.42683C9.4242 9.06136 9.3848 8.42942 9.75026 8.01535C10.2985 7.3942 11.1038 7 12 7C13.6569 7 15 8.34315 15 10C15 11.3072 14.1647 12.4171 13 12.829V13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13V12.5C11 11.6284 11.6873 11.112 12.2482 10.9692C12.681 10.859 13 10.4655 13 10C13 9.44772 12.5523 9 12 9ZM12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17H12.01C12.5623 17 13.01 16.5523 13.01 16C13.01 15.4477 12.5623 15 12.01 15H12Z"
                  fill="#323232"
                ></path>{' '}
              </g>
            </svg>
          </Button>

          <Button variant="outline-secondary" onClick={handleClose}>
            Close
          </Button>

          <Button
            variant="outline-primary"
            disabled={currentState === 1}
            onClick={() => handlePrevious()}
          >
            Previous
          </Button>

          <Button
            type="button"
            variant="outline-primary"
            disabled={
              (currentState === 1 && selectedLandParcels.length === 0) ||
              (currentState === 2 && plantingDataSource.length === 0)
            }
            onClick={handleSubmit(submit)}
          >
            {currentState < steps.length ? 'Next' : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Modal 1 */}
      <Modal show={showHelpModal1} onHide={closeHelpModal1} centered>
        <Modal.Body>
          <div>
            <h3>Selecting Land Boundary</h3>
            <ol>
              <li>On the map, click on the land boundary to choose it.</li>
              <li>Clicking on the land boundary again deselects it.</li>
            </ol>

            <h3>Custom Land Boundary</h3>
            <ol>
              <li>
                Use the polygon tools{' '}
                <svg height="15px" width="15px" viewBox="0 0 60 60">
                  <g>
                    <path d="M59.662,26.042L30.701,0.458c-0.377-0.332-0.94-0.334-1.319-0.004L0.343,25.79c-0.306,0.267-0.42,0.692-0.289,1.076 l11,32.249c0.138,0.405,0.519,0.677,0.946,0.677h35.954c0.427,0,0.806-0.271,0.945-0.674l11.046-32 C60.077,26.735,59.966,26.311,59.662,26.042z"></path>{' '}
                  </g>
                </svg>{' '}
                <svg viewBox="0 0 24 24" height="18px" width="18px">
                  <g>
                    <path d="M3,20V4A1,1,0,0,1,4,3H20a1,1,0,0,1,1,1V20a1,1,0,0,1-1,1H4A1,1,0,0,1,3,20Z"></path>
                  </g>
                </svg>{' '}
                on the map to draw a custom land.
              </li>
              <li>Click on next once you are done drawing the polygon.</li>
              <li>
                You can either remove the polygon by clicking on it after
                drawing or using the{' '}
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="trash-can"
                  className="svg-inline--fa fa-trash-can "
                  viewBox="0 0 448 512"
                >
                  <path
                    fill="currentColor"
                    d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0H284.2c12.1 0 23.2 6.8 28.6 17.7L320 32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 96 0 81.7 0 64S14.3 32 32 32h96l7.2-14.3zM32 128H416V448c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z"
                  ></path>
                </svg>{' '}
                icon.
              </li>
            </ol>
            <h3>Tips</h3>
            <ol>
              <li>
                Use{' '}
                <svg viewBox="0 0 24 24" fill="none" height="15px" width="15px">
                  <g>
                    {' '}
                    <path
                      d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z"
                      stroke="#000000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>{' '}
                  </g>
                </svg>{' '}
                to navigate the map with address/postal code.
              </li>
              <li>
                After defining a land boundary, clicking on the{' '}
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  className="svg-inline--fa fa-arrows-to-dot "
                  role="img"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="currentColor"
                    d="M256 0c17.7 0 32 14.3 32 32V64h32c12.9 0 24.6 7.8 29.6 19.8s2.2 25.7-6.9 34.9l-64 64c-12.5 12.5-32.8 12.5-45.3 0l-64-64c-9.2-9.2-11.9-22.9-6.9-34.9s16.6-19.8 29.6-19.8h32V32c0-17.7 14.3-32 32-32zM169.4 393.4l64-64c12.5-12.5 32.8-12.5 45.3 0l64 64c9.2 9.2 11.9 22.9 6.9 34.9s-16.6 19.8-29.6 19.8H288v32c0 17.7-14.3 32-32 32s-32-14.3-32-32V448H192c-12.9 0-24.6-7.8-29.6-19.8s-2.2-25.7 6.9-34.9zM32 224H64V192c0-12.9 7.8-24.6 19.8-29.6s25.7-2.2 34.9 6.9l64 64c12.5 12.5 12.5 32.8 0 45.3l-64 64c-9.2 9.2-22.9 11.9-34.9 6.9s-19.8-16.6-19.8-29.6V288H32c-17.7 0-32-14.3-32-32s14.3-32 32-32zm297.4 54.6c-12.5-12.5-12.5-32.8 0-45.3l64-64c9.2-9.2 22.9-11.9 34.9-6.9s19.8 16.6 19.8 29.6v32h32c17.7 0 32 14.3 32 32s-14.3 32-32 32H448v32c0 12.9-7.8 24.6-19.8 29.6s-25.7 2.2-34.9-6.9l-64-64zM256 224a32 32 0 1 1 0 64 32 32 0 1 1 0-64z"
                  ></path>
                </svg>{' '}
                icon centers the land boundary on the map.
              </li>
            </ol>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeHelpModal1}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Modal 2 */}
      <Modal show={showHelpModal2} onHide={closeHelpModal2} centered>
        <Modal.Body>
          <div>
            <h3>Plant trees instructions</h3>
            <ol>
              <li>Select the item you want to develop.</li>
              <li>
                Draw a boundary of where you would like the development to take
                place on the map.
              </li>
              <li>Fill in the necessasry details and click Save.</li>
            </ol>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeHelpModal2}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

CreateProjectModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default CreateProjectModal;
